import moment from 'moment';
import createReducer from '../../common/util/createReducer';
import {InvoiceLoad, Parts} from '../../common/actionTypes';
import {noTimeZoneShortISODate} from '../../common/constants/datesFormat';
import {TOYOTA_RUC} from '../../common/constants/dealer';

const today = moment();

const FORM_INITIAL_STATE = {
  'saving': false,
  'cancelling': false,
  'invoiceSavedOrCancelled': false,
  'action': '',
  'pageSize': 20,
  'pageNumber': 0,
  'values': {
    'dealerId': TOYOTA_RUC.toString(),
    'invoiceLoadType': 'M',
    'invoiceDate': today.format(noTimeZoneShortISODate),
    'boardingDate': today.format(noTimeZoneShortISODate),
    'transportation': 'A',
    'invoiceStatus': '',
    'detail': [],
  },
};

const addTotalWeight = (list) => {
  const newList = [...list];
  newList.forEach((element) => {
    element.totalWeight =
      parseInt(element.quantity, 10) * parseFloat(element.weight);
  });
  return newList;
};

const getDetailWithTotalsUpdated = (
  list,
  {index, quantity, unitPrice, weight}
) => {
  const newList = [...list];
  const newObject = {...newList[index]};

  if (quantity) {
    newObject.quantity = parseInt(quantity, 10);
  }
  if (unitPrice) {
    newObject.unitPrice = parseFloat(unitPrice);
  }
  if (weight) {
    newObject.weight = parseFloat(weight);
  }
  if (newObject.quantity && newObject.unitPrice) {
    const total = newObject.quantity * newObject.unitPrice;
    newObject.amount = total.toFixed(6);
  }
  if (newObject.quantity && newObject.weight) {
    const totalWeight = newObject.quantity * newObject.weight;
    newObject.totalWeight = totalWeight.toFixed(6);
  }
  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
};

const getDetailWithPartNameUpdated = (list, index, partData) => {
  const newList = [...list];
  const newObject = {...newList[index]};
  if (partData && partData.name) {
    newObject.partName = partData.name;
  }
  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
};

export const invoiceForm = (state, action) => {
  return createReducer(FORM_INITIAL_STATE, {
    [InvoiceLoad.LOAD_INITIAL_DATA]: (state, action) => ({
      ...state,
      'saving': false,
      'cancelling': false,
      'invoiceSavedOrCancelled': false,
      'action': '',
      'pageSize': 20,
      'pageNumber': 0,
      'values': {
        ...state.values,
        'invoiceNumber': '',
        'dealerId': TOYOTA_RUC.toString(),
        'providerId': '',
        'invoiceLoadType': 'M',
        'invoiceDate': today.format(noTimeZoneShortISODate),
        'boardingDate': today.format(noTimeZoneShortISODate),
        'transportation': 'A',
        'invoiceStatus': '',
        'detail': [],
      },
    }),
    [InvoiceLoad.FETCHED_INVOICE_DATA]: (state, action) => ({
      ...state,
      'saving': false,
      'cancelling': false,
      'invoiceSavedOrCancelled': false,
      'pageSize': 20,
      'pageNumber': 0,
      'values': {
        ...state.values,
        'invoiceNumber': action.response.data.invoiceNumber,
        'dealerId': action.response.data.dealerRuc,
        'providerId': action.response.data.providerId,
        'invoiceLoadType': 'M',
        'invoiceDate': action.response.data.invoiceDate ?
          moment(action.response.data.invoiceDate).format(noTimeZoneShortISODate) :
          null,
        'boardingDate': action.response.data.boardingDate ?
          moment(action.response.data.boardingDate).format(noTimeZoneShortISODate) :
          null,
        'transportation': action.response.data.transportType,
        'invoiceStatus': action.response.data.invoiceStatus,
        'detail': action.response.data.detail,
      },
    }),
    [InvoiceLoad.FETCHED_INVOICE_DATA_FAILED]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'invoiceNumber': '',
        'dealerId': '',
        'providerId': '',
        'invoiceLoadType': 'M',
        'invoiceDate': null,
        'boardingDate': null,
        'transportation': '',
        'detail': [],
      },
    }),
    [InvoiceLoad.SAVE_EXCEL_DATA]: (state, action) => ({
      ...state,
      'pageSize': 20,
      'pageNumber': 0,
      'values': {
        ...state.values,
        'invoiceLoadType': 'A',
        'detail': addTotalWeight(action.payload),
      },
    }),
    [InvoiceLoad.CHANGE_DETAILS_IN_INVOICES_LIST_PAGE]: (state, action) => ({
      ...state,
      'pageNumber': action.payload,
    }),
    [InvoiceLoad.CHANGE_DETAILS_IN_INVOICES_LIST_PAGE_SIZE]: (
      state,
      action
    ) => ({
      ...state,
      'pageSize': action.payload,
      'pageNumber': 0,
    }),
    [InvoiceLoad.ADD_ITEM_IN_LAST_PAGE]: (state, action) => ({
      ...state,
      'pageNumber': action.payload,
    }),

    [InvoiceLoad.UPDATE_TOTALS]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'detail': getDetailWithTotalsUpdated(state.values.detail, action.payload),
      },
    }),
    [Parts.FETCHED_PART]: (state, action) => {
      return {
        ...state,
        'values': {
          ...state.values,
          'detail': getDetailWithPartNameUpdated(
            state.values.detail,
            action.payload,
            action.response.data
          ),
        },
      };
    },
    [InvoiceLoad.CREATING_PROVIDER_INVOICES]: (state, action) => ({
      ...state,
      'saving': true,
    }),
    [InvoiceLoad.CREATED_PROVIDER_INVOICES]: (state, action) => ({
      ...state,
      'saving': false,
      'invoiceSavedOrCancelled': true,
      'action': action.payload,
      'values': {
        ...state.values,
        'invoiceStatus': action.response.data.invoiceStatus,
      },
    }),
    [InvoiceLoad.CREATED_PROVIDER_INVOICES_FAILED]: (state, action) => ({
      ...state,
      'saving': false,
    }),
    [InvoiceLoad.CANCELLING_INVOICE]: (state, action) => ({
      ...state,
      'cancelling': true,
    }),
    [InvoiceLoad.CANCELLED_INVOICE]: (state, action) => ({
      ...state,
      'invoiceSavedOrCancelled': true,
      'action': 'cancel',
      'cancelling': false,
    }),
    [InvoiceLoad.CANCELLED_INVOICE_FAILED]: (state, action) => ({
      ...state,
      'cancelling': false,
    }),
  })(state, action);
};
